<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import type { Trip } from "../../lib/types";
  import { addNotification } from "../../lib/stores";
  import { formatCurrency } from "../../lib/utils/format";
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Modal from "../../components/overlay/Modal.svelte";
  import EmptyState from "../../components/feedback/EmptyState.svelte";
  import Button from "../../components/input/Button.svelte";

  interface Accommodation {
    id: string;
    name: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    check_in_date: string;
    check_out_date: string;
    check_in_time?: string;
    check_out_time?: string;
    confirmation_number?: string;
    price_cents: number;
    price_currency: string;
    nights: number;
    booking_status: string;
    booking_platform?: string;
    booking_url?: string;
    notes?: string;
    phone?: string;
  }

  // ========== 지도 관련 (더미 구현) ==========
  let showMapModal = $state(false);
  let selectedAccommodation = $state<Accommodation | null>(null);

  // 더미 Google Maps API 키 - 실제 구현 시 환경변수에서 로드
  const GOOGLE_MAPS_API_KEY = "YOUR_GOOGLE_MAPS_API_KEY";

  function openMapModal(acc: Accommodation) {
    selectedAccommodation = acc;
    showMapModal = true;
  }

  function closeMapModal() {
    showMapModal = false;
    selectedAccommodation = null;
  }

  // 더미 지도 URL 생성 (Google Maps Static API 또는 embed)
  function getMapEmbedUrl(acc: Accommodation): string {
    if (acc.latitude && acc.longitude) {
      // 좌표가 있으면 사용
      return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${acc.latitude},${acc.longitude}&zoom=15`;
    } else if (acc.address) {
      // 주소로 검색
      return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(acc.address)}&zoom=15`;
    }
    return "";
  }

  // Google Maps 열기 (외부 링크)
  function openInGoogleMaps(acc: Accommodation) {
    let url = "https://www.google.com/maps/search/?api=1";
    if (acc.latitude && acc.longitude) {
      url += `&query=${acc.latitude},${acc.longitude}`;
    } else if (acc.address) {
      url += `&query=${encodeURIComponent(acc.address)}`;
    } else {
      url += `&query=${encodeURIComponent(acc.name)}`;
    }
    window.open(url, "_blank");
  }

  let { trip, accommodations = [], user = null }: {
    trip: Trip;
    accommodations?: Accommodation[];
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  let showModal = $state(false);
  let editingAccommodation = $state<Accommodation | null>(null);
  let loading = $state(false);

  let form = $state({
    name: "",
    address: "",
    check_in_date: "",
    check_out_date: "",
    check_in_time: "15:00",
    check_out_time: "11:00",
    confirmation_number: "",
    price_cents: 0,
    price_currency: "KRW",
    booking_status: "pending",
    booking_platform: "",
    booking_url: "",
    notes: "",
    phone: ""
  });

  const bookingPlatforms = [
    { value: "", label: "선택 안함" },
    { value: "booking.com", label: "Booking.com" },
    { value: "airbnb", label: "Airbnb" },
    { value: "agoda", label: "Agoda" },
    { value: "hotels.com", label: "Hotels.com" },
    { value: "expedia", label: "Expedia" },
    { value: "yanolja", label: "야놀자" },
    { value: "goodchoice", label: "여기어때" },
    { value: "other", label: "기타" }
  ];

  const bookingStatuses = [
    { value: "pending", label: "예약 대기", color: "bg-yellow-100 text-yellow-700" },
    { value: "confirmed", label: "예약 완료", color: "bg-green-100 text-green-700" },
    { value: "cancelled", label: "취소됨", color: "bg-red-100 text-red-700" }
  ];

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  }

  function getStatusInfo(status: string) {
    return bookingStatuses.find(s => s.value === status) || bookingStatuses[0];
  }

  function openCreateModal() {
    editingAccommodation = null;
    form = {
      name: "",
      address: "",
      check_in_date: trip.startDate,
      check_out_date: trip.endDate,
      check_in_time: "15:00",
      check_out_time: "11:00",
      confirmation_number: "",
      price_cents: 0,
      price_currency: "KRW",
      booking_status: "pending",
      booking_platform: "",
      booking_url: "",
      notes: "",
      phone: ""
    };
    showModal = true;
  }

  function openEditModal(acc: Accommodation) {
    editingAccommodation = acc;
    form = {
      name: acc.name,
      address: acc.address || "",
      check_in_date: acc.check_in_date,
      check_out_date: acc.check_out_date,
      check_in_time: acc.check_in_time || "15:00",
      check_out_time: acc.check_out_time || "11:00",
      confirmation_number: acc.confirmation_number || "",
      price_cents: acc.price_cents / 100,
      price_currency: acc.price_currency,
      booking_status: acc.booking_status,
      booking_platform: acc.booking_platform || "",
      booking_url: acc.booking_url || "",
      notes: acc.notes || "",
      phone: acc.phone || ""
    };
    showModal = true;
  }

  async function saveAccommodation() {
    loading = true;
    try {
      const payload = {
        ...form,
        price_cents: form.price_cents * 100
      };

      const url = editingAccommodation
        ? `/api/v1/trips/${trip.id}/accommodations/${editingAccommodation.id}`
        : `/api/v1/trips/${trip.id}/accommodations`;

      const response = await fetch(url, {
        method: editingAccommodation ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accommodation: payload })
      });

      if (response.ok) {
        addNotification("success", editingAccommodation ? "숙소가 수정되었습니다." : "숙소가 추가되었습니다.");
        showModal = false;
        router.reload();
      } else {
        const data = await response.json();
        addNotification("error", data.error || "저장에 실패했습니다.");
      }
    } catch (error) {
      addNotification("error", "저장에 실패했습니다.");
    } finally {
      loading = false;
    }
  }

  async function deleteAccommodation(id: string) {
    if (!confirm("이 숙소를 삭제하시겠습니까?")) return;

    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/accommodations/${id}`, {
        method: "DELETE"
      });

      if (response.ok) {
        addNotification("success", "숙소가 삭제되었습니다.");
        router.reload();
      }
    } catch (error) {
      addNotification("error", "삭제에 실패했습니다.");
    }
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app">
    <TripSubNav {trip} currentPage="accommodations" />

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-primary">숙소</h1>
          <p class="text-secondary">여행 중 투숙할 숙소를 관리하세요</p>
        </div>
        <Button variant="primary" onclick={openCreateModal}>숙소 추가</Button>
      </div>

      {#if accommodations.length === 0}
        <EmptyState
          icon="trip"
          title="등록된 숙소가 없습니다"
          description="여행 중 투숙할 숙소를 추가해보세요!"
        >
          <Button variant="primary" onclick={openCreateModal}>숙소 추가하기</Button>
        </EmptyState>
      {:else}
        <!-- Timeline View -->
        <div class="space-y-4">
          {#each accommodations as acc}
            {@const statusInfo = getStatusInfo(acc.booking_status)}
            <div class="bg-surface rounded-2xl shadow-sm border border-default overflow-hidden hover:shadow-md transition-shadow">
              <div class="p-6">
                <div class="flex items-start justify-between">
                  <div class="flex items-start gap-4">
                    <!-- Icon -->
                    <div class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                    </div>
                    
                    <div>
                      <div class="flex items-center gap-2">
                        <h3 class="text-lg font-semibold text-primary">{acc.name}</h3>
                        <span class="px-2 py-0.5 rounded-full text-xs font-medium {statusInfo.color}">
                          {statusInfo.label}
                        </span>
                      </div>
                      
                      {#if acc.address}
                        <p class="text-sm text-muted mt-1 flex items-center gap-1">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <span class="truncate max-w-xs">{acc.address}</span>
                          <button
                            onclick={() => openMapModal(acc)}
                            class="ml-1 text-blue-600 hover:text-blue-700 hover:underline text-xs"
                          >
                            지도보기
                          </button>
                        </p>
                      {/if}

                      <div class="flex items-center gap-4 mt-3 text-sm">
                        <div class="flex items-center gap-1 text-secondary">
                          <span class="font-medium">체크인</span>
                          <span>{formatDate(acc.check_in_date)} {acc.check_in_time || ""}</span>
                        </div>
                        <span class="text-muted">→</span>
                        <div class="flex items-center gap-1 text-secondary">
                          <span class="font-medium">체크아웃</span>
                          <span>{formatDate(acc.check_out_date)} {acc.check_out_time || ""}</span>
                        </div>
                        <span class="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs font-medium">
                          {acc.nights}박
                        </span>
                      </div>

                      {#if acc.confirmation_number}
                        <p class="text-sm text-muted mt-2">
                          예약번호: <span class="font-mono">{acc.confirmation_number}</span>
                        </p>
                      {/if}
                    </div>
                  </div>

                  <div class="text-right">
                    <p class="text-lg font-bold text-primary">₩{formatCurrency(acc.price_cents)}</p>
                    <p class="text-xs text-muted">{acc.nights}박 총액</p>
                    
                    <div class="flex gap-1 mt-3">
                      {#if acc.booking_url}
                        <a href={acc.booking_url} target="_blank" aria-label="예약 페이지 열기" class="p-2 text-muted hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      {/if}
                      <button aria-label="숙소 편집" onclick={() => openEditModal(acc)} class="p-2 text-muted hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button aria-label="숙소 삭제" onclick={() => deleteAccommodation(acc.id)} class="p-2 text-muted hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {#if acc.notes}
                  <div class="mt-4 p-3 bg-app rounded-lg text-sm text-secondary">
                    {acc.notes}
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>

        <!-- Summary -->
        <div class="mt-8 bg-surface rounded-2xl p-6 shadow-sm border border-default">
          <h3 class="font-semibold text-primary mb-4">숙박 요약</h3>
          <div class="grid grid-cols-3 gap-4">
            <div class="text-center p-4 bg-purple-50 rounded-xl">
              <p class="text-2xl font-bold text-purple-600">{accommodations.length}</p>
              <p class="text-sm text-secondary">숙소</p>
            </div>
            <div class="text-center p-4 bg-blue-50 rounded-xl">
              <p class="text-2xl font-bold text-blue-600">
                {accommodations.reduce((sum, a) => sum + a.nights, 0)}박
              </p>
              <p class="text-sm text-secondary">총 숙박</p>
            </div>
            <div class="text-center p-4 bg-green-50 rounded-xl">
              <p class="text-2xl font-bold text-green-600">
                ₩{formatCurrency(accommodations.reduce((sum, a) => sum + a.price_cents, 0))}
              </p>
              <p class="text-sm text-secondary">총 비용</p>
            </div>
          </div>
        </div>
      {/if}
    </main>

    <!-- Map Modal -->
    {#if showMapModal && selectedAccommodation}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onclick={closeMapModal}>
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <div class="bg-surface rounded-2xl w-full max-w-2xl overflow-hidden" onclick={(e) => e.stopPropagation()}>
          <div class="p-4 border-b border-bright flex items-center justify-between">
            <div>
              <h3 class="font-semibold text-primary">{selectedAccommodation.name}</h3>
              {#if selectedAccommodation.address}
                <p class="text-sm text-muted">{selectedAccommodation.address}</p>
              {/if}
            </div>
            <button aria-label="지도 닫기" onclick={closeMapModal} class="p-2 text-muted hover:text-secondary rounded-lg hover:bg-surface-hover">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- 더미 지도 영역 -->
          <div class="h-80 bg-surface-hover flex items-center justify-center">
            {#if selectedAccommodation.address || (selectedAccommodation.latitude && selectedAccommodation.longitude)}
              <!-- 실제 구현 시 Google Maps iframe 또는 Kakao Map 사용 -->
              <div class="text-center">
                <div class="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p class="text-secondary mb-4">지도 기능은 준비 중입니다</p>
                <p class="text-sm text-muted mb-4">Google Maps API 키 설정 후 사용 가능합니다</p>
              </div>
            {:else}
              <p class="text-muted">주소 정보가 없습니다</p>
            {/if}
          </div>

          <div class="p-4 border-t border-bright flex justify-end gap-3">
            <button
              onclick={() => selectedAccommodation && openInGoogleMaps(selectedAccommodation)}
              class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Google Maps에서 열기
            </button>
          </div>
        </div>
      </div>
    {/if}

    <!-- Modal -->
    <Modal show={showModal} title={editingAccommodation ? "숙소 수정" : "숙소 추가"} size="lg" onClose={() => showModal = false}>
      <form onsubmit={(e) => { e.preventDefault(); saveAccommodation(); }} class="space-y-4">
        <div>
          <label for="acc-name" class="label">숙소명 *</label>
          <input id="acc-name" type="text" bind:value={form.name} class="input" placeholder="호텔/에어비앤비 이름" required />
        </div>

        <div>
          <label for="acc-address" class="label">주소</label>
          <input id="acc-address" type="text" bind:value={form.address} class="input" placeholder="숙소 주소" />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="acc-checkin-date" class="label">체크인 날짜 *</label>
            <input id="acc-checkin-date" type="date" bind:value={form.check_in_date} class="input" required />
          </div>
          <div>
            <label for="acc-checkout-date" class="label">체크아웃 날짜 *</label>
            <input id="acc-checkout-date" type="date" bind:value={form.check_out_date} class="input" min={form.check_in_date} required />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="acc-checkin-time" class="label">체크인 시간</label>
            <input id="acc-checkin-time" type="time" bind:value={form.check_in_time} class="input" />
          </div>
          <div>
            <label for="acc-checkout-time" class="label">체크아웃 시간</label>
            <input id="acc-checkout-time" type="time" bind:value={form.check_out_time} class="input" />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="acc-price" class="label">총 숙박비</label>
            <input id="acc-price" type="number" bind:value={form.price_cents} class="input" placeholder="0" min="0" />
          </div>
          <div>
            <label for="acc-status" class="label">예약 상태</label>
            <select id="acc-status" bind:value={form.booking_status} class="input">
              {#each bookingStatuses as status}
                <option value={status.value}>{status.label}</option>
              {/each}
            </select>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="acc-platform" class="label">예약 플랫폼</label>
            <select id="acc-platform" bind:value={form.booking_platform} class="input">
              {#each bookingPlatforms as platform}
                <option value={platform.value}>{platform.label}</option>
              {/each}
            </select>
          </div>
          <div>
            <label for="acc-confirmation" class="label">예약 확인번호</label>
            <input id="acc-confirmation" type="text" bind:value={form.confirmation_number} class="input" placeholder="예약 확인번호" />
          </div>
        </div>

        <div>
          <label for="acc-url" class="label">예약 링크</label>
          <input id="acc-url" type="url" bind:value={form.booking_url} class="input" placeholder="https://..." />
        </div>

        <div>
          <label for="acc-phone" class="label">연락처</label>
          <input id="acc-phone" type="tel" bind:value={form.phone} class="input" placeholder="숙소 전화번호" />
        </div>

        <div>
          <label for="acc-notes" class="label">메모</label>
          <textarea id="acc-notes" bind:value={form.notes} class="input" rows="2" placeholder="체크인 방법, 와이파이 비밀번호 등"></textarea>
        </div>

        <div class="flex gap-3 pt-4">
          <Button type="button" variant="secondary" class="flex-1" onclick={() => showModal = false}>취소</Button>
          <Button type="submit" variant="primary" class="flex-1" loading={loading}>{editingAccommodation ? "수정하기" : "추가하기"}</Button>
        </div>
      </form>
    </Modal>
  </div>
</Layout>
